import { useState } from 'react';
import { css } from '@emotion/react';
import {
  DynamicFieldKey_Enum,
  TemplateType_Slug,
  Template,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { useQuery } from 'react-query';
import { QuickFillApi } from '@frontend/api-quick-fill';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { TemplatorV2Queries } from '@frontend/api-templator-v2';
import { useTranslation } from '@frontend/i18n';
import { TemplateSelector, useGetDefaultTemplate } from '@frontend/integrated-messaging';
import { useAppScopeStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import {
  Button,
  MessageTemplate,
  Modal,
  Stepper,
  TagType,
  Text,
  useAlert,
  UseFormReturnType,
  useModalControl,
} from '@frontend/design-system';
import { formatDateTime } from '../../../helpers';
import { QUICK_FILL_TRACKING_IDS } from '../../../tracking-ids';
import { SpinnerLoader } from '../components/spinning-loader';
import { useQuickFillTrayContext } from '../context';
import { useQuickFillRecipientShallowStore } from '../store';
import { convertToIsoString, defaultQuickFillTemplate } from './utils';
import { SendMessagesFormConfig } from '.';

dayjs.extend(timezone);

type Props = {
  formValues: UseFormReturnType<SendMessagesFormConfig, keyof SendMessagesFormConfig>['values'];
  appointmentSlotCount: number;
};

const trackingIds = QUICK_FILL_TRACKING_IDS.sendMessagesTab.composeMessageStep;

export const ComposeMessage = ({ formValues, appointmentSlotCount }: Props) => {
  const alert = useAlert();
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const { t } = useTranslation('schedule-quick-fill');
  const { closeModal } = useQuickFillTrayContext();
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  const locationId = formValues.location || '';
  const locationTimezone = accessibleLocationData?.[locationId]?.timezone;

  const getDefaultTemplate = useGetDefaultTemplate({ groupIds: selectedLocationIds });

  const defaultTemplate = getDefaultTemplate({ type: TemplateType_Slug.MANUAL_SCHEDULE_QUICK_FILL });

  const [template, setTemplate] = useState<string>(defaultTemplate?.templateString || defaultQuickFillTemplate);

  const [selectedTemplate, setSelectedTemplate] = useState<Template>();

  const { selectedIds } = useQuickFillRecipientShallowStore('selectedIds');

  const messageTemplatesModalControls = useModalControl();

  const { mutateAsync: sendMessage, isLoading: isSending } = ScheduleQueries.useSendQuickFillMessage();

  const { data: manualTemplateData, refetch: refetchManualTemplates } = useQuery({
    queryKey: ['scheduleQuickFillListTemplate', locationId],
    queryFn: () => QuickFillApi.getDefaultTemplate(locationId),
    enabled: !!locationId,
  });

  const templateTypeQuery = TemplatorV2Queries.useTemplateTypesList(
    {
      businessGroupIds: selectedLocationIds,
      templateTypeSlugs: [TemplateType_Slug.MANUAL_SCHEDULE_QUICK_FILL],
    },
    {
      select: (data) =>
        data.templateTypes
          .find((template) => template.slug === TemplateType_Slug.MANUAL_SCHEDULE_QUICK_FILL)
          ?.dynamicFields.reduce<TagType[]>((acc, dynamicField) => {
            const { label, key, exampleValues } = dynamicField;
            if (label && key) {
              acc.push({
                label,
                key: `{{${key}}}`,
                value: exampleValues?.[0] ?? '',
                optional: !dynamicField.isRequired,
                nonRemovable: !!dynamicField.isRequired,
              });
            }
            return acc;
          }, []) ?? [],
      enabled: !!selectedLocationIds.length,
    }
  );

  const templateTags = templateTypeQuery.data ?? [];

  const isLoadingTemplateData = templateTypeQuery.isLoading;

  const sendQuickFillMessage = async (firstSlotDateTime: string) => {
    await sendMessage({
      locationId,
      personIds: selectedIds,
      startTime: firstSlotDateTime,
      endTime: firstSlotDateTime, // End time is mandatory for the API, but not used in the message anymore
    });
    alert.success(t('Quick Fill message sent successfully'));
    closeModal();
  };

  const getSortedAppointmentSlots = () => {
    const slots = Array.from({ length: appointmentSlotCount }, (_, i) => {
      const index = i + 1;
      const dateFieldKey = `date${index}` as keyof SendMessagesFormConfig;
      const timeFieldKey = `time${index}` as keyof SendMessagesFormConfig;
      const date = formValues[dateFieldKey];
      const time = formValues[timeFieldKey];
      const datetime =
        !isScheduleV3FlagEnabled && locationTimezone
          ? dayjs(`${date} ${time}`, 'MM/DD/YYYY HH:mm:ss').tz(locationTimezone)
          : dayjs(`${date} ${time}`, 'MM/DD/YYYY HH:mm:ss');
      return { date, time, datetime };
    });

    // Sort slots by datetime asc
    return slots.sort((a, b) => a.datetime.valueOf() - b.datetime.valueOf());
  };

  const getTemplateWithAppointmentSlots = (
    originalTemplate: string,
    slots: {
      date?: string;
      time?: string;
      datetime: dayjs.Dayjs;
    }[] = []
  ) => {
    if (appointmentSlotCount === 1) return originalTemplate;

    const dateTimeList = slots
      .map((slot, idx) => {
        const number = idx + 1;
        if (idx === 0) {
          return `${number}. {date_time}`; // First slot uses template tag as backend will insert the first date here
        }
        const formattedDate = slot.datetime.format('ddd, MMMM D');
        const formattedTime = slot.datetime.format('h:mmA');
        return `${number}. ${formattedTime} on ${formattedDate} `;
      })
      .join('\n');

    return originalTemplate.replace('{date_time}', `\n${dateTimeList}`);
  };

  const handleSubmit = async () => {
    if (!(selectedIds.length && locationId)) return;

    const sortedSlots = getSortedAppointmentSlots();
    const formattedTemplate = formatTemplateForUpdateOnly(template);
    const templateWithSlots = getTemplateWithAppointmentSlots(formattedTemplate, sortedSlots);

    const isUpdateTemplate = manualTemplateData?.Template !== formattedTemplate || appointmentSlotCount > 1;

    if (isUpdateTemplate) {
      await QuickFillApi.updateTemplate(templateWithSlots, locationId);
    }

    try {
      const firstSlotDateTime = isScheduleV3FlagEnabled
        ? formatDateTime(`${sortedSlots[0].date} ${sortedSlots[0].time}`, {
            fromTimezone: locationTimezone,
            toTimezone: 'UTC',
            outputFormat: 'YYYY-MM-DDTHH:mm:ss.000[Z]',
          })
        : convertToIsoString(sortedSlots[0].date, sortedSlots[0].time);
      await sendQuickFillMessage(firstSlotDateTime);
    } catch (error) {
      console.error('Error sending Quick Fill message:', error);
      alert.error(t('Failed to send Quick Fill message'));
      sentry.error({ team: 'schedule', topic: 'scheduling', error });
    }

    if (isUpdateTemplate) {
      refetchManualTemplates();
    }
  };

  const formatTemplateForUpdateOnly = (template: string) => {
    // Handle specific dynamic field key mappings to return the correct key content and format for the quick fill api - single braces, lowercase, and different text.
    return template.replaceAll(/\{([^{}]+)\}/g, (_group, content) => {
      const upperCaseContent = content.toUpperCase();
      if (upperCaseContent === DynamicFieldKey_Enum.BUSINESS_GROUP_NAME) {
        return 'practice_name';
      }
      if (upperCaseContent === DynamicFieldKey_Enum.DATETIME_FORMAL) {
        return 'date_time';
      }
      if (upperCaseContent === DynamicFieldKey_Enum.BUSINESS_GROUP_PHONE) {
        return 'practice_phone';
      }
      return content.toLowerCase();
    });
  };

  const messageTemplateModalTitle = t('Select a Quick Fill Template');

  if (isLoadingTemplateData) return <SpinnerLoader />;

  return (
    <>
      <MessageTemplate
        draggableTags
        tags={templateTags ?? []}
        initialTemplate={
          selectedTemplate?.templateString || defaultTemplate?.templateString || defaultQuickFillTemplate
        }
        onChange={(template) => setTemplate(template)}
        isNewTag
      >
        <div css={containerStyle}>
          <MessageTemplate.Editor />
          <Text size='small' color='subdued'>
            {t('Click a tag below to add it to your message')}
          </Text>
          <MessageTemplate.TagList />
        </div>
      </MessageTemplate>
      <Stepper.ButtonBar
        helperText={template.length === 0 ? t('Compose a message') : ''}
        css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: theme.spacing(2) }}
      >
        <Button
          iconName='template'
          variant='secondary'
          size='large'
          css={{ border: 'none', padding: theme.spacing(1, 0.5) }}
          {...messageTemplatesModalControls.triggerProps}
          trackingId={trackingIds.openTemplateSelectionButton}
        ></Button>
        <Stepper.NextButton
          trackingId={trackingIds.sendButton}
          isValid={template.length > 0 && !isSending}
          onSubmit={handleSubmit}
        >
          {t('Send')}
        </Stepper.NextButton>
      </Stepper.ButtonBar>
      <Modal
        {...messageTemplatesModalControls.modalProps}
        onClose={messageTemplatesModalControls.closeModal}
        maxWidth={600}
      >
        <Modal.Header title={messageTemplateModalTitle} onClose={messageTemplatesModalControls.closeModal}>
          {messageTemplateModalTitle}
        </Modal.Header>
        <Modal.Body>
          <TemplateSelector
            groupIds={selectedLocationIds}
            templateTypes={[TemplateType_Slug.MANUAL_SCHEDULE_QUICK_FILL]}
            onSelectTemplate={(template) => {
              setTemplate(template.templateString ?? '');
              setSelectedTemplate(template);
              messageTemplatesModalControls.closeModal();
            }}
            onOpenSettings={messageTemplatesModalControls.closeModal}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
`;
